import React, { useMemo, useRef, useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  StyleProp,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';

export interface GlassSelectOption<TValue extends string | number = string> {
  label: string;
  value: TValue;
}

export interface GlassSelectProps<TValue extends string | number = string> {
  label?: string;
  selectedValue?: TValue | null;
  options: GlassSelectOption<TValue>[];
  onChange: (value: TValue) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  selectorStyle?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  maxDropdownHeight?: number;
}

export function GlassSelect<TValue extends string | number = string>({
  label,
  selectedValue,
  options,
  onChange,
  placeholder = 'Select',
  required,
  error,
  disabled = false,
  containerStyle,
  selectorStyle,
  labelStyle,
  maxDropdownHeight = 320,
}: GlassSelectProps<TValue>) {
  const styles = useStyles();
  const { colors } = useThemeColors();
  const [isOpen, setIsOpen] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);
  const anchorRef = useRef<any>(null);

  const selectedLabel = useMemo(() => {
    const found = options.find(
      (o) => String(o.value) === String(selectedValue ?? ''),
    );
    return found?.label ?? '';
  }, [options, selectedValue]);

  const handlePress = useCallback(() => {
    if (disabled) return;
    if (Platform.OS === 'web' && anchorRef.current?.getBoundingClientRect) {
      const rect = anchorRef.current.getBoundingClientRect();
      setAnchor({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
    } else {
      setAnchor({ x: 24, y: 120, w: 320, h: 44 });
    }
    setIsOpen(true);
  }, [disabled]);

  return (
    <View style={[styles.container, containerStyle]}>
      {label ? (
        <Text style={[styles.label, labelStyle]}>
          {label}
          {required ? <Text style={styles.required}> *</Text> : null}
        </Text>
      ) : null}

      <Hoverable
        hover="field"
        ref={anchorRef}
        style={[
          styles.selector,
          error && styles.selectorError,
          disabled && styles.selectorDisabled,
          selectorStyle,
        ]}
        onPress={handlePress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen, disabled }}
      >
        <Text
          numberOfLines={1}
          style={[styles.selectedText, !selectedLabel && styles.placeholderText]}
        >
          {selectedLabel || placeholder}
        </Text>
        <SVG_COMPONENTS.CHEVRON_DOWN width={18} height={18} color={colors.text.subtle} />
      </Hoverable>

      {error ? <Text style={styles.errorText}>{error}</Text> : null}

      <AnchoredDropdown
        isOpen={isOpen}
        anchor={anchor}
        width={anchor?.w ?? 320}
        maxHeight={maxDropdownHeight}
        onRequestClose={() => setIsOpen(false)}
      >
        <View style={styles.dropdown}>
          <ScrollView
            style={{ maxHeight: maxDropdownHeight }}
            contentContainerStyle={{ paddingVertical: 6 }}
          >
            {options.map((opt) => {
              const isActive = String(opt.value) === String(selectedValue ?? '');
              return (
                <Hoverable
                  key={`opt-${String(opt.value)}`}
                  hover={isActive ? 'none' : 'row'}
                  style={[styles.optionRow, isActive && styles.optionRowActive]}
                  onPress={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  accessibilityRole="button"
                  accessibilityState={{ selected: isActive }}
                >
                  <Text
                    style={[styles.optionText, isActive && styles.optionTextActive]}
                  >
                    {opt.label}
                  </Text>
                </Hoverable>
              );
            })}
          </ScrollView>
        </View>
      </AnchoredDropdown>
    </View>
  );
}

GlassSelect.displayName = 'GlassSelect';

const useStyles = () =>
  useThemedStyles(({ components, semantic, colors }) =>
    StyleSheet.create({
      container: {
        marginBottom: 16,
      },
      label: {
        fontSize: 14,
        fontWeight: '500',
        color: components.admin.formInput.label,
        marginBottom: 8,
      },
      required: {
        color: semantic.status.error,
      },
      selector: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: components.admin.selector.bg,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: components.admin.selector.border,
        paddingHorizontal: 12,
        paddingVertical: 12,
        minHeight: 48,
      },
      selectorError: {
        borderColor: semantic.status.error,
      },
      selectorDisabled: {
        opacity: 0.55,
      },
      selectedText: {
        flex: 1,
        fontSize: 16,
        color: colors.text.primary,
        marginRight: 8,
      },
      placeholderText: {
        color: colors.text.subtle,
      },
      errorText: {
        fontSize: 12,
        color: semantic.status.error,
        marginTop: 4,
      },
      dropdown: {
        backgroundColor: components.admin.selector.dropdownBg,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: components.admin.selector.border,
        overflow: 'hidden',
      },
      optionRow: {
        paddingHorizontal: 12,
        paddingVertical: 10,
      },
      optionRowActive: {
        backgroundColor: components.admin.selector.itemSelectedBg,
      },
      optionText: {
        fontSize: 14,
        color: colors.text.primary,
      },
      optionTextActive: {
        color: components.admin.selector.itemSelectedText,
        fontWeight: '600',
      },
    }),
  );
